import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Location } from '@angular/common';
import { Product } from '../_models/product';
import { ProductService } from '../_services/product.service';
import { Globals } from '../globals';
import { environment } from '../../environments/environment';
import { ProductContent } from '../_models/product-content';
import { MatSnackBar } from '@angular/material/snack-bar';
import { switchMap, catchError } from 'rxjs/operators';
import _ from 'lodash';
import { forkJoin, concat, of } from 'rxjs';
import { ProductPrice } from '../_models/product-price';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ImageUploadDialogComponent } from '../image-upload-dialog/image-upload-dialog.component';

@Component({
  selector: 'app-product-view',
  templateUrl: './product-view.component.html',
  styleUrls: ['./product-view.component.scss'],
})
export class ProductViewComponent implements OnInit {
  public categories = new FormControl();
  public isDataLoaded = false;
  public isModified = false;
  public newProductContent = new ProductContent();
  public product: Product;
  public showAddContents = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    public productService: ProductService,
    public globals: Globals,
    private location: Location,
    public snackBar: MatSnackBar,
    public uploadDialog: MatDialog,
  ) { }

  public activate(): void {
    this.product.Active = true;
    this.save();
  }

  public addContent(): void {
    console.log(this.newProductContent);
    if (!this.newProductContent.ProductId || this.newProductContent.ProductId <= 0) {
      this.newProductContent.ProductId = this.product.Id;
    }
    this.productService
      .addProductContents(this.newProductContent)
      .then((p) => {
        this.product.Content.push(p);
        this.cancelAddContent();
      })
      .catch((err) => {
        console.error(err);
        this.showErrorMessage('Sorry, your request cannot be process at the moment!');
      });
  }

  public back(): void {
    this.location.back();
  }

  public cancel(): void {
    this.refresh();
  }

  public cancelAddContent(): void {
    this.showAddContents = false;
    this.newProductContent = new ProductContent();
    this.newProductContent.ProductId = this.product.Id;
  }

  public changeValue(price: ProductPrice, property: string, event: any) {
    console.log(event);
    price[property] = event.target.textContent;
  }

  public dataChanged(): void {
    this.isModified = true;
  }

  public deactivate(): void {
    this.product.Active = false;
    this.save();
  }

  public delete() {
    this.productService
      .deleteProduct(this.product)
      .then((res) => {
        this.refresh();
      })
      .catch((err) => {
        console.error(err);
        this.showErrorMessage('Sorry, your request cannot be process at the moment!');
      });
  }

  public deleteContent(id): void {
    if (confirm('Are you sure to delete this item?')) {
      this.productService
        .deleteProductContents(this.product.Id, id)
        .then((res) => {
          _.remove(this.product.Content, {
            Id: res.Id,
          });
        })
        .catch((err) => {
          console.error(err);
          this.showErrorMessage('Sorry, your request cannot be process at the moment!');
        });
    }
  }

  public getImageUrl(): string {
    return environment.imageUrl;
  }

  public loadData(productId: number) {
    const obs = forkJoin({
      product: this.productService.getProductById(productId),
      prodContents: this.productService.getProductContents(productId),
      pricesPerOrigin: this.productService.getProductPrices(productId),
      prodCategories: this.productService.getProductCategories(productId),
    });
    obs.subscribe(({ product, prodContents, pricesPerOrigin, prodCategories }) => {
      this.product = product;
      this.product.Content = prodContents;
      this.categories.setValue(
        prodCategories.map((value, index, array) => {
          return value.CategoryId;
        }),
        { emitModelToViewChange: true },
      );
      this.product.Categories = prodCategories.map((value, index, array) => {
        return value.CategoryId;
      });
      pricesPerOrigin.forEach((price, index, array) => {
        if (this.globals.countries[price.CountryId]) {
          price.CountryCode = this.globals.countries[price.CountryId].Code;
        }
      });
      this.product.Prices = pricesPerOrigin;
      this.product.Prices.push(new ProductPrice());
      this.isDataLoaded = true;
      this.isModified = false;
    });
  }

  public ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          const productId = params.get('id');
          if (!this.globals.isNullOrEmpty(productId)) {
            this.loadData(parseInt(productId, 10));
          } else {
            this.showErrorMessage('Please specify the product Id!');
          }

          return Promise.resolve();
        }),
      )
      .subscribe();
  }

  public refresh(): void {
    this.isDataLoaded = false;
    this.loadData(this.product.Id);
  }

  public save(): void {
    this.product.Weight = Math.round(this.product.Weight);
    concat(this.productService.updateProduct(this.product), this.saveCategories()).subscribe(
      (res) => {
        this.refresh();
      },
      (err) => {
        console.error(err);
        this.showErrorMessage('Sorry, your request cannot be process at the moment!');
      },
    );
  }

  private saveCategories() {
    const categories = {};
    const removedCategories = this.product.Categories.filter((categoryId) => {
      return !this.categories.value.includes(categoryId);
    });
    this.categories.value.forEach((value, index, array) => {
      const category = {
        ProductId: this.product.Id,
        CategoryId: value,
      };
      categories[index] = this.productService.saveProductCategory(category).pipe(
        catchError((err) => {
          if (err.error.Message !== 'Category already exists for this product') {
            console.error(err);
            throw err;
          }
          return of({});
        }),
      );
    });
    removedCategories.forEach((value, index, array) => {
      categories[index + 100] = this.productService.removeProductCategory(value, this.product.Id).pipe(
        catchError((err) => {
          console.error(err);
          return of({});
        }),
      );
    });
    return forkJoin(categories);
  }

  public saveProductPrice(price: ProductPrice, event: Event) {
    event.preventDefault();
    if (!price.ProductId) {
      price.ProductId = this.product.Id;
    }
    this.productService.saveProductPrice(price).subscribe(
      (resultPrice) => {
        this.refresh();
      },
      (error) => {
        console.error(error);
        this.refresh();
      },
    );
  }

  public showErrorMessage(message): void {
    this.snackBar.open(message, 'Error', {
      duration: 2000,
    });
  }

  public showUploadDialog(event: Event) {
    // event.preventDefault();
    const dialogRef = this.uploadDialog.open(ImageUploadDialogComponent);
    dialogRef.afterClosed().subscribe((result) => {
      // console.log(`Dialog result: ${result}`);
      if (result) {
        this.product.ImageUrl = result;
        this.dataChanged();
      }
    });
  }

  public updateList(price: ProductPrice, propertys: string, value: any) {
    console.log(value);
  }
}
